// AGC: ConsultantProfile - Full consultant detail page
// AGC: Shows complete bio, reviews, consultation types, and request button

import React, { useState, useEffect } from 'react'
import { 
  Star, 
  CheckCircle, 
  Clock, 
  MessageCircle,
  MapPin,
  Award,
  Phone,
  FileText,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type ConsultantProfile as ConsultantProfileType,
  SPECIALIZATION_INFO,
  CONSULTATION_TYPE_INFO,
  LEVEL_INFO,
  type ConsultationType,
} from '../../types/advisor'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'

interface ConsultantProfileProps {
  consultantId?: string
  onNavigate: (screen: Screen, params?: Record<string, unknown>) => void
  onBack: () => void
}

const BACKEND_URL = getBackendUrl()

/**
 * AGC: Consultant Profile Component
 */
export const ConsultantProfile: React.FC<ConsultantProfileProps> = ({
  consultantId,
  onNavigate,
  onBack,
}) => {
  const { t } = useTranslation()
  
  const [consultant, setConsultant] = useState<ConsultantProfileType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchConsultant = async () => {
      if (!consultantId) {
        setError('ID do consultor não fornecido')
        setIsLoading(false)
        return
      }
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/v1/advisors/${consultantId}`)
        const data = await response.json()
        
        if (data.success) {
          setConsultant(data.consultant)
        } else {
          setError(data.error || 'Erro ao carregar perfil')
        }
      } catch (err) {
        console.error('Error fetching consultant:', err)
        setError('Erro de conexão')
      } finally {
        setIsLoading(false)
      }
    }

    fetchConsultant()
  }, [consultantId])

  const handleRequestConsultation = (type?: ConsultationType) => {
    onNavigate('consultation-request' as Screen, { 
      consultantId, 
      consultationType: type 
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    )
  }

  if (error || !consultant) {
    return (
      <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900">
        <TopBar title="Perfil do Consultor" onBack={onBack} />
        <div className="p-4">
          <GlassCard>
            <div className="text-center py-6">
              <p className="text-red-500">{error || 'Consultor não encontrado'}</p>
              <button onClick={onBack} className="mt-4 text-emerald-600">
                Voltar
              </button>
            </div>
          </GlassCard>
        </div>
      </div>
    )
  }

  const levelInfo = LEVEL_INFO[consultant.level]

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('advisor.profile') || 'Perfil do Consultor'}
        onBack={onBack}
      />

      {/* AGC: Header with cover and avatar */}
      <div className="relative">
        <div className="h-32 bg-gradient-to-br from-emerald-500 to-teal-600" />
        
        <div className="px-4 -mt-16">
          <div className="flex items-end gap-4">
            {/* AGC: Avatar */}
            <div className="relative">
              {consultant.photoURL ? (
                <img
                  src={consultant.photoURL}
                  alt={consultant.displayName}
                  className="w-28 h-28 rounded-2xl object-cover border-4 border-white dark:border-slate-900"
                />
              ) : (
                <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 border-4 border-white dark:border-slate-900 flex items-center justify-center">
                  <span className="text-white font-bold text-4xl">
                    {consultant.displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {consultant.isVerified && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center border-3 border-white dark:border-slate-900">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
              )}
            </div>

            {/* AGC: Name and level */}
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {consultant.displayName}
                </h1>
                <span className="text-lg">{levelInfo.badge}</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                CREA: {consultant.creaRegistration} / {consultant.creaState}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AGC: Stats row */}
      <div className="px-4 mt-4">
        <div className="flex items-center justify-around p-4 bg-white dark:bg-slate-800 rounded-xl">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {consultant.rating.toFixed(1)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Avaliação</p>
          </div>
          
          <div className="w-px h-10 bg-gray-200 dark:bg-slate-700" />
          
          <div className="text-center">
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {consultant.totalConsultations}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">Consultas</p>
          </div>
          
          <div className="w-px h-10 bg-gray-200 dark:bg-slate-700" />
          
          <div className="text-center">
            <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {consultant.yearsExperience}
            </span>
            <p className="text-xs text-gray-500 dark:text-gray-400">Anos exp.</p>
          </div>
        </div>
      </div>

      {/* AGC: Content */}
      <div className="px-4 py-6 space-y-6 pb-32">
        {/* AGC: About */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.about') || 'Sobre'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {consultant.bio}
          </p>
        </GlassCard>

        {/* AGC: Specializations */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.specializations') || 'Especializações'}
          </h3>
          <div className="flex flex-wrap gap-2">
            {consultant.specializations.map(spec => {
              const info = SPECIALIZATION_INFO[spec]
              return (
                <span
                  key={spec}
                  className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-lg text-sm font-medium"
                >
                  {info?.icon} {info?.name || spec}
                </span>
              )
            })}
          </div>
        </GlassCard>

        {/* AGC: Services offered */}
        <GlassCard padding="none">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t('advisor.services') || 'Serviços Oferecidos'}
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {consultant.consultationTypesOffered?.map(type => {
              const info = CONSULTATION_TYPE_INFO[type]
              return (
                <button
                  key={type}
                  onClick={() => handleRequestConsultation(type)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {info.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {info.description}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>R$ {info.priceRange.min} - {info.priceRange.max}</span>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {info.responseTime}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </button>
              )
            })}
          </div>
        </GlassCard>

        {/* AGC: Additional info */}
        <GlassCard>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
            {t('advisor.additionalInfo') || 'Informações Adicionais'}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
              <Clock className="w-5 h-5" />
              <span>Tempo de resposta: {consultant.responseTime}</span>
            </div>
            {consultant.location && (
              <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                <MapPin className="w-5 h-5" />
                <span>{consultant.location}</span>
              </div>
            )}
            <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
              <Award className="w-5 h-5" />
              <span>{consultant.yearsExperience} anos de experiência</span>
            </div>
            {consultant.availableForRemote && (
              <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                <MessageCircle className="w-5 h-5" />
                <span>Atendimento remoto disponível</span>
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* AGC: Fixed CTA */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pt-4 pb-safe-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">A partir de</p>
            <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
              R$ {consultant.priceRange.min}
            </p>
          </div>
          <button
            onClick={() => handleRequestConsultation()}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
          >
            Solicitar Consulta
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConsultantProfile

